import * as React from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, ChevronLeft, ChevronRight, Trash2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useBackStackEntry } from "@/lib/navigation";

/** Full-screen, continuous multi-shot camera session - no official shadcn
 *  "camera capture" component exists in the base registry, same situation
 *  Dropzone itself was in, so this follows that same hand-built-but-in-
 *  components/ui precedent. Always mounted with an already-live `stream`
 *  (requested by the caller, see dropzone.tsx) - this component never
 *  requests or retries getUserMedia itself, so it has no loading/error
 *  state of its own: by the time it renders, the camera already works. */
export interface CameraCaptureProps {
  stream: MediaStream;
  /** Cancelled - zero photos committed, camera released. */
  onClose: () => void;
  /** "Done" pressed with at least one photo - camera released, photos handed
   *  off as plain Files ready for Dropzone's own onFilesSelected contract. */
  onCapture: (files: File[]) => void;
}

interface Capture {
  file: File;
  previewUrl: string;
}

let nextSequence = 0;

const CameraCapture = React.forwardRef<HTMLDivElement, CameraCaptureProps>(
  ({ stream, onClose, onCapture }, ref) => {
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [captures, setCaptures] = React.useState<Capture[]>([]);
    // Index into `captures` of the photo currently shown full-screen; null =
    // preview closed (the normal viewfinder is showing instead).
    const [previewIndex, setPreviewIndex] = React.useState<number | null>(
      null
    );
    // Read inside cleanup/unmount effects without re-subscribing them to
    // every capture-list change - only the final list at exit time matters.
    const capturesRef = React.useRef<Capture[]>([]);
    capturesRef.current = captures;
    // Drives the non-blocking "Discard N photo(s)?" ConfirmDialog, shown
    // before actually closing whenever photos exist. NOT window.confirm():
    // that's a synchronous, thread-blocking native dialog, and Android's
    // gesture-nav back is handled by the browser chrome independently of
    // page JS - a second swipe while confirm() was still up was observed to
    // reach the router before confirm() ever returned, unwinding the whole
    // wizard underneath the camera. A React-state dialog has no such gap:
    // useBackStackEntry re-arms its guard entry synchronously in the same
    // popstate tick that opens it (see that hook), before the dialog's own
    // first paint, so there is never a moment where back can slip past
    // unguarded.
    const [discardPending, setDiscardPending] = React.useState(false);

    React.useEffect(() => {
      const video = videoRef.current;
      if (video) video.srcObject = stream;
      // Releasing the camera (stopping every track) must happen on EVERY
      // exit path - Done, X/cancel, AND an unrelated unmount (e.g.
      // navigating away from the Bills step mid-session) - not just the
      // two explicit buttons below. .stop() on an already-stopped track is
      // a no-op, so overlapping this with cleanupAndClose's own stop calls
      // is safe, not wasteful. Revoking every outstanding preview URL here
      // too covers the same abnormal-unmount case for that leak.
      return () => {
        stream.getTracks().forEach((t) => t.stop());
        capturesRef.current.forEach((c) => URL.revokeObjectURL(c.previewUrl));
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream]);

    // A phone's swipe-back/hardware-back gesture is browser navigation, not
    // a click this component sees - without its own entry on the shared
    // back-handler stack, the router would treat it as "leave this page" and
    // pop the whole wizard route out from under the camera. useBackStackEntry
    // (lib/navigation.ts) is the one place in the app allowed to touch
    // window.history/popstate for this - it owns a single shared listener so
    // a layered overlay like this one (camera, then preview on top, then
    // this discard dialog on top of that) coordinates with page-level back
    // handling instead of racing it with a second, independent listener.
    // Each layer below registers itself in mount order (camera first), so a
    // gesture dismisses the TOPMOST active one first - preview back closes
    // the preview back to the camera; the dialog back closes just the
    // dialog; camera back (dialog not open) asks to discard, or closes
    // outright with zero photos.
    useBackStackEntry(true, true, () => {
      if (capturesRef.current.length > 0) {
        setDiscardPending(true);
      } else {
        closeCameraRef.current();
      }
    });
    useBackStackEntry(previewIndex !== null, true, () => setPreviewIndex(null));
    useBackStackEntry(discardPending, true, () => setDiscardPending(false));

    const addCaptureFromBlob = (blob: Blob) => {
      nextSequence += 1;
      const file = new File(
        [blob],
        `bill-photo-${Date.now()}-${nextSequence}.jpg`,
        { type: blob.type || "image/jpeg", lastModified: Date.now() }
      );
      setCaptures((prev) => [
        ...prev,
        { file, previewUrl: URL.createObjectURL(blob) }
      ]);
    };

    // Grabs the current video frame via <canvas> at whatever resolution the
    // live PREVIEW stream happens to be running at - this is the fallback
    // path, used whenever ImageCapture isn't available or takePhoto() fails.
    const captureFromVideoFrame = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.videoWidth === 0) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (blob) addCaptureFromBlob(blob);
        },
        "image/jpeg",
        0.8
      );
    };

    // The preview stream is deliberately left at the browser's default,
    // lower resolution (see dropzone.tsx) so the continuous live viewfinder
    // stays smooth - a 4K+ preview was noticeably laggier to decode/render
    // every frame. A captured STILL needs the opposite: full sharpness, one
    // frame, latency doesn't matter. ImageCapture.takePhoto() asks the
    // camera hardware for a full-resolution still independent of whatever
    // resolution the live track is actually streaming at, same as a native
    // camera app's shutter - so sharpness and preview smoothness are no
    // longer the same tradeoff. Where ImageCapture doesn't exist (older iOS
    // Safari) or takePhoto() itself rejects, this falls back to grabbing
    // whatever frame the preview is currently showing, exactly as before.
    const capturePhoto = () => {
      const ImageCaptureCtor = (
        window as unknown as {
          ImageCapture?: new (track: MediaStreamTrack) => {
            takePhoto: () => Promise<Blob>;
          };
        }
      ).ImageCapture;
      const [track] = stream.getVideoTracks();
      if (!ImageCaptureCtor || !track) {
        captureFromVideoFrame();
        return;
      }
      new ImageCaptureCtor(track)
        .takePhoto()
        .then(addCaptureFromBlob)
        .catch(captureFromVideoFrame);
    };

    const removeCapture = (index: number) => {
      setCaptures((prev) => {
        URL.revokeObjectURL(prev[index].previewUrl);
        return prev.filter((_, i) => i !== index);
      });
    };

    // Deleting from inside the full-screen preview needs to also move (or
    // close) the preview itself, since the index it's showing may shift or
    // disappear once the list shrinks - plain removeCapture alone would
    // leave previewIndex pointing at the wrong photo or past the end.
    const deleteFromPreview = (index: number) => {
      removeCapture(index);
      setPreviewIndex((current) => {
        if (current === null) return current;
        const remaining = captures.length - 1;
        if (remaining <= 0) return null;
        return Math.min(current, remaining - 1);
      });
    };

    // Shared by Done and X/cancel - both release the camera and revoke
    // every remaining preview URL; only whether onCapture also fires differs.
    const cleanupAndClose = () => {
      stream.getTracks().forEach((t) => t.stop());
      capturesRef.current.forEach((c) => URL.revokeObjectURL(c.previewUrl));
    };

    // The bare close, no dialog, no discard question - correct whenever
    // there's genuinely nothing to lose (zero captures) or the user has
    // already confirmed the discard via the dialog below.
    const closeCamera = () => {
      setDiscardPending(false);
      cleanupAndClose();
      onClose();
    };
    const closeCameraRef = React.useRef(closeCamera);
    closeCameraRef.current = closeCamera;

    // The red X / top-level cancel: only asks when there's something to
    // lose. Unlike the back-gesture path in the popstate handler above,
    // there's no history race here at all - this is a plain click, so there
    // is nothing to re-arm before opening the dialog.
    const handleCancelButtonClick = () => {
      if (captures.length > 0) {
        setDiscardPending(true);
      } else {
        closeCamera();
      }
    };

    const handleDone = () => {
      if (captures.length === 0) return;
      const files = captures.map((c) => c.file);
      cleanupAndClose();
      onCapture(files);
    };

    const preview = previewIndex !== null ? captures[previewIndex] : null;

    // Nothing behind the camera may scroll or bounce while it's open (iOS
    // rubber-bands the document on a drag even over a fixed overlay).
    React.useEffect(() => {
      const html = document.documentElement;
      const { overflow: bodyOverflow } = document.body.style;
      const { overscrollBehavior } = html.style;
      document.body.style.overflow = "hidden";
      html.style.overscrollBehavior = "none";
      return () => {
        document.body.style.overflow = bodyOverflow;
        html.style.overscrollBehavior = overscrollBehavior;
      };
    }, []);

    // Portalled to <body>: rendered inside the wizard, the camera sat in the
    // app's scrolling content area, where iOS Safari paints a sticky,
    // backdrop-blurred element (the wizard's phone Back/Next bar) over a
    // fixed overlay once that area is dragged - whatever their z-index.
    // touch-none stops a drag on the camera scrolling anything underneath;
    // the thumbnail strip opts back in to sideways swiping (touch-pan-x).
    return createPortal(
      <>
      <div
        ref={ref}
        className="fixed inset-0 z-50 flex h-dvh w-full touch-none flex-col bg-black"
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="min-h-0 flex-1 object-cover"
        />
        <canvas ref={canvasRef} className="hidden" />

        <div className="flex items-center justify-center px-4 pt-[calc(0.75rem+env(safe-area-inset-top))]">
          <span className="rounded-full bg-black/40 px-3 py-1 text-sm text-white">
            {captures.length} photo{captures.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="flex flex-col gap-3 bg-black/60 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3">
          {captures.length > 0 && (
            <div className="flex touch-pan-x gap-2 overflow-x-auto overscroll-contain pt-2 pr-2">
              {captures.map((c, i) => (
                <div
                  key={c.previewUrl}
                  className="relative h-16 w-16 shrink-0"
                >
                  <button
                    type="button"
                    onClick={() => setPreviewIndex(i)}
                    aria-label={`View captured bill ${i + 1}`}
                    className="h-full w-full overflow-hidden rounded-md border border-white/30"
                  >
                    <img
                      src={c.previewUrl}
                      alt={`Captured bill ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCapture(i);
                    }}
                    aria-label={`Remove captured bill ${i + 1}`}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-black shadow"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-10">
            <button
              type="button"
              onClick={handleCancelButtonClick}
              aria-label="Cancel"
              className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive text-destructive-foreground"
            >
              <X className="h-7 w-7" />
            </button>
            <button
              type="button"
              onClick={capturePhoto}
              aria-label="Take photo"
              className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-white/20"
            >
              <span className="h-16 w-16 rounded-full bg-white" />
            </button>
            <button
              type="button"
              onClick={handleDone}
              disabled={captures.length === 0}
              aria-label="Done"
              className={cn(
                "flex h-16 w-16 items-center justify-center rounded-full text-primary-foreground",
                captures.length === 0 ? "bg-primary/40" : "bg-primary"
              )}
            >
              <CheckCircle2 className="h-7 w-7" />
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[60] flex h-dvh w-full touch-none flex-col bg-black">
          <div className="flex items-center justify-between px-4 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <button
              type="button"
              onClick={() => setPreviewIndex(null)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close preview</span>
            </button>
            <span className="rounded-full bg-black/40 px-3 py-1 text-sm text-white">
              {previewIndex! + 1} of {captures.length}
            </span>
            <button
              type="button"
              onClick={() => deleteFromPreview(previewIndex!)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white"
            >
              <Trash2 className="h-5 w-5" />
              <span className="sr-only">Delete this photo</span>
            </button>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center">
            <img
              src={preview.previewUrl}
              alt={`Captured bill ${previewIndex! + 1}`}
              className="max-h-full max-w-full object-contain"
            />
            {previewIndex! > 0 && (
              <button
                type="button"
                onClick={() => setPreviewIndex((i) => (i ?? 0) - 1)}
                aria-label="Previous photo"
                className="absolute left-2 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            {previewIndex! < captures.length - 1 && (
              <button
                type="button"
                onClick={() => setPreviewIndex((i) => (i ?? 0) + 1)}
                aria-label="Next photo"
                className="absolute right-2 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>

          <div className="flex justify-center pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3">
            <button
              type="button"
              onClick={() => setPreviewIndex(null)}
              className="rounded-full bg-primary px-6 py-2 text-sm font-medium text-white"
            >
              Back to camera
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={discardPending}
        onOpenChange={(open) => {
          // Dismissed WITHOUT confirming (Cancel, backdrop tap, Escape) -
          // just close the dialog; the useBackStackEntry(discardPending, …)
          // call above notices `active` turning false and consumes its own
          // now-unneeded guard entry (see that hook's docstring) - including
          // when THIS dismissal came from a back gesture, which already
          // marks the entry consumedByPop so that cleanup is a no-op there.
          if (!open) setDiscardPending(false);
        }}
        title="Discard photos?"
        description={`Discard ${captures.length} photo(s)? This can't be undone.`}
        confirmLabel="Discard"
        destructive
        onConfirm={() => closeCameraRef.current()}
      />
      </>,
      document.body
    );
  }
);
CameraCapture.displayName = "CameraCapture";

export { CameraCapture };
